import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconDeviceFloppy,
  IconFileText,
  IconMaximize,
  IconQuote,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Page from '../../../../components/Page/Page';
import { useAuth } from '../../../../contexts/AuthProvider';
import { useI18n } from '../../../../contexts/I18nProvider';
import useCompany from '../../../../hooks/useCompany';
import request from '../../../../services/AxiosClient';
import PayslipOcrService from '../../../../services/PayslipOcrService';
import {
  OcrExtractedPayslip,
  OcrPayslipJobItem,
  UserPayslipResponseStatus,
} from '../../../../types/types';
import handleErrorMessage from '../../../../utils/handleErrorMessage';
import s from './PayslipOcrReview.module.sass';

const LOW_CONFIDENCE_THRESHOLD = 0.7;

const ZOOM_STEP = 25;
const ZOOM_MIN = 50;
const ZOOM_MAX = 200;

type FieldDescriptor = {
  key: keyof OcrExtractedPayslip;
  labelKey: string;
  type: 'text' | 'number' | 'date';
};

const FIELDS: FieldDescriptor[] = [
  { key: 'lastName', labelKey: 'lastname', type: 'text' },
  { key: 'firstName', labelKey: 'firstname', type: 'text' },
  { key: 'matricule', labelKey: 'matricule', type: 'text' },
  { key: 'companyCode', labelKey: 'companyCode', type: 'text' },
  { key: 'siret', labelKey: 'siret', type: 'text' },
  { key: 'payPeriodStart', labelKey: 'payPeriodStart', type: 'date' },
  { key: 'payPeriodEnd', labelKey: 'payPeriodEnd', type: 'date' },
  { key: 'grossSalary', labelKey: 'grossSalary', type: 'number' },
  { key: 'netSalary', labelKey: 'netSalary', type: 'number' },
  { key: 'leaveBalanceN', labelKey: 'leaveBalanceN', type: 'number' },
  {
    key: 'leaveBalanceNMinus1',
    labelKey: 'leaveBalanceNMinus1',
    type: 'number',
  },
  { key: 'rttBalance', labelKey: 'rttBalance', type: 'number' },
];

function matchStatusColor(status: UserPayslipResponseStatus): string {
  switch (status) {
    case UserPayslipResponseStatus.OK:
      return 'green';
    case UserPayslipResponseStatus.NOTFOUND:
      return 'red';
    default:
      return 'orange';
  }
}

type Props = {
  /** Entreprise cliente ciblée quand l'écran est embarqué dans la vue gestionnaire de paie. */
  customerCompanyId?: string;
  /**
   * Rendu sans le shell `Page` : la revue s'affiche en place dans l'écran OCR imbriqué, avec un
   * en-tête compact au lieu du fil d'Ariane. Implique que `jobId` et `itemIndex` sont fournis en
   * props — il n'y a pas de route dédiée pour les porter.
   */
  inline?: boolean;
  /** Job et item fournis directement en mode inline (pas de route dédiée). */
  jobId?: string;
  itemIndex?: number;
  /** Retour vers l'écran OCR ; par défaut, navigation vers la page dédiée. */
  onBack?: () => void;
};

export default function PayslipOcrReview({
  customerCompanyId,
  inline = false,
  jobId: jobIdProp,
  itemIndex,
  onBack,
}: Props) {
  const { t } = useI18n();
  const { user } = useAuth();
  const { id: companyId, company } = useCompany(user, customerCompanyId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const jobId = jobIdProp ?? params.jobId;
  const itemIdx = itemIndex ?? Number(params.idx);

  const [item, setItem] = useState<OcrPayslipJobItem | null>(null);
  const [draft, setDraft] = useState<OcrExtractedPayslip>({});
  const [loadingJob, setLoadingJob] = useState(false);
  const [saving, setSaving] = useState(false);

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [fullscreenOpened, fullscreen] = useDisclosure(false);

  const backToOcr = useCallback(
    () => (onBack ? onBack() : navigate('/end-of-month/payslips/ocr')),
    [navigate, onBack]
  );

  // ---------- Chargement du job + de l'item ----------

  const loadJob = useCallback(async () => {
    if (!jobId || !companyId || Number.isNaN(itemIdx)) return;
    setLoadingJob(true);
    try {
      const job = await PayslipOcrService.getJob(companyId, jobId);
      const it = job?.items?.[itemIdx];
      if (!it) {
        showNotification({
          id: 'error-ocrItemNotFound',
          title: t('w.error'),
          message: t('payslipOcr.review.itemNotFound'),
          color: 'red',
          icon: <IconX />,
        });
        setItem(null);
        return;
      }
      setItem(it);
      setDraft(it.extractedFields ? { ...it.extractedFields } : {});
    } catch (error) {
      console.error('Failed to load OCR job', error);
      showNotification({
        id: 'error-loadOcrJob',
        title: t('payslipOcr.progress.fetchError'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setLoadingJob(false);
    }
  }, [companyId, jobId, itemIdx]);

  useEffect(() => {
    void loadJob();
  }, [loadJob]);

  // ---------- Chargement du PDF source ----------
  // Le JWT doit passer en header : on télécharge le binaire via axios puis on crée un object URL
  // (un iframe pointant directement sur l'endpoint ne serait pas authentifié).

  useEffect(() => {
    if (!jobId || !companyId || Number.isNaN(itemIdx)) return;
    let objectUrl: string | null = null;
    let cancelled = false;

    async function fetchPdf() {
      setIsLoadingPdf(true);
      try {
        const url = PayslipOcrService.getItemSourceUrl(
          companyId,
          jobId!,
          itemIdx
        );
        const response = await request.get(url, {
          responseType: 'arraybuffer',
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' })
        );
        setBlobUrl(objectUrl);
      } catch (error) {
        console.error('Failed to fetch OCR source PDF', error);
        if (cancelled) return;
        setBlobUrl(null);
        showNotification({
          id: 'error-loadOcrPdf',
          title: t('w.error'),
          message: t('payslipOcr.review.pdfError'),
          color: 'red',
          icon: <IconX />,
        });
      } finally {
        if (!cancelled) setIsLoadingPdf(false);
      }
    }

    void fetchPdf();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [companyId, jobId, itemIdx]);

  // ---------- Helpers ----------

  function confidenceFor(field: keyof OcrExtractedPayslip): number | null {
    const c = item?.extractedFields?.confidence?.[field as string];
    return typeof c === 'number' ? c : null;
  }

  function isLowConfidence(field: keyof OcrExtractedPayslip): boolean {
    const c = confidenceFor(field);
    return c !== null && c < LOW_CONFIDENCE_THRESHOLD;
  }

  function rawMatchFor(field: keyof OcrExtractedPayslip): string | null {
    return item?.extractedFields?.rawMatch?.[field as string] ?? null;
  }

  const pageTitle = useMemo(() => {
    if (!item) return t('payslipOcr.review.title');
    const name =
      item.extractedFields?.lastName || item.extractedFields?.firstName
        ? `${item.extractedFields.lastName ?? ''} ${
            item.extractedFields.firstName ?? ''
          }`.trim()
        : t('payslipOcr.progress.unnamedItem', itemIdx + 1);
    return `${t('payslipOcr.review.title')} — ${name}`;
  }, [item, itemIdx]);

  // ---------- Sauvegarde ----------

  async function save() {
    if (!jobId || !companyId || Number.isNaN(itemIdx)) return;
    setSaving(true);
    try {
      await PayslipOcrService.updateItem(companyId, jobId, itemIdx, draft);
      showNotification({
        id: 'success-saveOcrItem',
        message: t('payslipOcr.review.saved'),
        color: 'green',
        icon: <IconCheck />,
      });
      // Le back a re-synchronisé le PayslipMonth : on invalide le cache react-query pour que
      // « Bulletins de paie » re-fetch les valeurs à la prochaine visite, sans F5 manuel.
      queryClient.invalidateQueries({
        queryKey: ['PayslipService.getPayslipsMonthInfo'],
      });
      await loadJob();
    } catch (error) {
      console.error('OCR item update failed', error);
      showNotification({
        id: 'error-saveOcrItem',
        title: t('payslipOcr.review.saveError'),
        message: handleErrorMessage(error, t),
        color: 'red',
        icon: <IconX />,
      });
    } finally {
      setSaving(false);
    }
  }

  function renderField(field: FieldDescriptor) {
    const label = (
      <Group gap="xs">
        <Text size="xs" fw={500}>
          {t(`payslipOcr.review.field.${field.labelKey}`)}
        </Text>
        {isLowConfidence(field.key) && (
          <Badge size="xs" variant="light" color="orange" radius="sm">
            {t('payslipOcr.review.lowConfidence')}
          </Badge>
        )}
      </Group>
    );
    const error = isLowConfidence(field.key);
    const rawMatch = rawMatchFor(field.key);

    return (
      <Stack key={field.key} gap={4}>
        {field.type === 'number' ? (
          <NumberInput
            label={label}
            value={(draft[field.key] as number | undefined) ?? ''}
            decimalScale={2}
            onChange={(value) =>
              setDraft((prev) => ({
                ...prev,
                [field.key]: value === '' ? undefined : Number(value),
              }))
            }
            error={error}
          />
        ) : (
          <TextInput
            label={label}
            value={(draft[field.key] as string | undefined) ?? ''}
            placeholder={field.type === 'date' ? 'DD/MM/YYYY' : undefined}
            onChange={(event) => {
              // La valeur est extraite AVANT l'updater : React remet `currentTarget` à null
              // dès la fin du handler, or l'updater fonctionnel de setState n'est pas
              // toujours exécuté de façon synchrone (le chemin « eager state » de React 18
              // n'est pris que lorsqu'aucun rendu n'est déjà planifié). Le lire dans
              // l'updater plantait donc dès la deuxième frappe.
              const { value } = event.currentTarget;
              setDraft((prev) => ({ ...prev, [field.key]: value }));
            }}
            error={error}
          />
        )}
        {rawMatch && (
          <Group gap={4} wrap="nowrap" className={s.rawMatch}>
            <IconQuote size={12} />
            <Text size="xs" c="dimmed" title={rawMatch}>
              {rawMatch}
            </Text>
          </Group>
        )}
      </Stack>
    );
  }

  function renderZoomControls() {
    return (
      <Group gap={4}>
        <Tooltip label={t('payslipOcr.review.zoomOut')}>
          <ActionIcon
            variant="subtle"
            disabled={zoomLevel <= ZOOM_MIN}
            onClick={() =>
              setZoomLevel((level) => Math.max(level - ZOOM_STEP, ZOOM_MIN))
            }
          >
            <IconZoomOut size={18} />
          </ActionIcon>
        </Tooltip>
        <Text size="sm" className={s.zoomLabel}>
          {zoomLevel}%
        </Text>
        <Tooltip label={t('payslipOcr.review.zoomIn')}>
          <ActionIcon
            variant="subtle"
            disabled={zoomLevel >= ZOOM_MAX}
            onClick={() =>
              setZoomLevel((level) => Math.min(level + ZOOM_STEP, ZOOM_MAX))
            }
          >
            <IconZoomIn size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  }

  const statusBadges = (
    <Group gap="xs">
      {item?.detectedEditor && (
        <Badge variant="light" color="blue" radius="sm">
          {item.detectedEditor}
        </Badge>
      )}
      {item?.matchStatus && (
        <Badge
          variant="light"
          color={matchStatusColor(item.matchStatus)}
          radius="sm"
        >
          {t('payslipOcr.review.match', item.matchStatus)}
        </Badge>
      )}
    </Group>
  );

  const content = (
    <>
      <div className={inline ? `${s.layout} ${s.layoutInline}` : s.layout}>
        {/* ─────────── Champs extraits ─────────── */}
        <Card withBorder radius="md" p="lg" className={s.fieldsPanel}>
          <Title order={4} mb="md">
            {t('payslipOcr.review.title')}
          </Title>

          {item?.errorMessage && (
            <Alert
              mb="md"
              variant="light"
              color="red"
              icon={<IconAlertTriangle />}
            >
              {item.errorMessage}
            </Alert>
          )}

          {loadingJob ? (
            <Group justify="center" p="md">
              <Loader />
            </Group>
          ) : (
            item && <Stack gap="sm">{FIELDS.map(renderField)}</Stack>
          )}

          <Group justify="flex-end" gap="xs" mt="lg">
            <Button variant="subtle" disabled={saving} onClick={backToOcr}>
              {t('w.cancel')}
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              disabled={!item || loadingJob}
              onClick={save}
            >
              {t('w.save')}
            </Button>
          </Group>
        </Card>

        {/* ─────────── Viewer PDF ─────────── */}
        <Card withBorder radius="md" p={0} className={s.viewer}>
          <Group justify="space-between" p="md">
            <Group gap="xs">
              <IconFileText size={20} />
              <Text fw={500}>{t('payslipOcr.review.sourceTitle')}</Text>
            </Group>
            <Group gap={4}>
              {renderZoomControls()}
              <Tooltip label={t('payslipOcr.review.fullscreen')}>
                <ActionIcon variant="subtle" onClick={fullscreen.open}>
                  <IconMaximize size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
          <Divider />
          <div className={s.viewerContent}>
            {isLoadingPdf ? (
              <Group justify="center" align="center" h="100%">
                <Loader />
              </Group>
            ) : blobUrl ? (
              <iframe
                src={blobUrl}
                title={pageTitle}
                className={s.iframe}
                style={{ transform: `scale(${zoomLevel / 100})` }}
              />
            ) : (
              <Group justify="center" align="center" h="100%">
                <Text c="dimmed">{t('payslipOcr.review.pdfError')}</Text>
              </Group>
            )}
          </div>
        </Card>
      </div>

      <Modal
        opened={fullscreenOpened}
        onClose={fullscreen.close}
        fullScreen
        title={
          <Group gap="xs">
            <IconFileText size={20} />
            <Text fw={500}>{pageTitle}</Text>
          </Group>
        }
        styles={{ body: { height: 'calc(100vh - 60px)', padding: 0 } }}
      >
        {blobUrl && (
          <iframe
            src={blobUrl}
            title={pageTitle}
            className={s.iframeFullscreen}
          />
        )}
      </Modal>
    </>
  );

  if (inline) {
    return (
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={backToOcr}
            >
              {t('payslipOcr.title')}
            </Button>
            <Text fw={500}>{pageTitle}</Text>
          </Group>
          {statusBadges}
        </Group>
        {content}
      </Stack>
    );
  }

  return (
    <Page
      title={pageTitle}
      parent={{
        label: company ? company.name : t('payslipOcr.title'),
        action: backToOcr,
      }}
      headerRightSection={statusBadges}
    >
      {content}
    </Page>
  );
}
